import isArray from 'lodash/isArray';
import mergeWith from 'lodash/mergeWith';
import toArray from 'lodash/toArray';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';

import validateDisabledNodes from './validateDisabledNodes';

import filterEmptyAndDisabled from '~lib/common/utils/filterEmptyAndDisabled';

import validateBehaviorNodes from './validateBehaviorNodes';
import validateControlTypes from './validateControlTypes';
import validateEmptyChecks from './validateEmptyChecks';
import validateEndConditions from './validateEndConditions';
import validateMedicationNodes from './validateMedicationNodes';
import validateInfusionNodes from './validateInfusionNodes';
import validateOxygenActions from './validateOxygenActions';
import validateOxygenChecks from './validateOxygenChecks';
import validateIVChecks from './validateIVChecks';
import validateInterventionNodes from './validateInterventionNodes';
import validateMigratedNodes from './validateMigratedNodes';
import validateMonitorNodes from './validateMonitorNodes';
import validateNodeConnections from './validateNodeConnections';
import validateLabMediaNodes from './validateLabMediaNodes';
import validateMoulageNodes from './validateMoulageNodes';
import validateProcedureControls from './validateProcedureControls';
import validateDialogueControls from './validateDialogueControls';
import validateTransferScene from './validateTransferScene';
import validateCustomStateNodes from './validateCustomStateNodes';
import validateGradeEffectNodes from './validateGradeEffectNodes';
import validateExposeNodes from './validateExposeNodes';
import validateCheckCounts from './validateCheckCounts';
import validatePulseStrengthChecks from './validatePulseStrengthChecks';
import validateGradingConflicts from './validateGradingConflicts';
import validateTriggeringActions from './validateTriggeringActions';
import validateCustomMenuNodes from './validateCustomMenuNodes';
import validateAiNodes from './validateAiNodes';

function validateCaseLogicDescription(
  caseConf,
  validationOptions = { isEffectChain: false, isMedicationEffectChain: false }
) {
  // Do validation of disabled nodes before filtering
  const disabledResults = validateDisabledNodes(caseConf);

  // Filter out disabled and empty nodes, plus their connections
  const cld = {
    nodes: cloneDeep(get(caseConf, 'caseLogicDescription.nodes'))
  }
  const options = {
    isAiEnabled: caseConf.dialogue.aiEnabled,
    keepOrphans: true
  };
  filterEmptyAndDisabled(cld, options);

  // Make structure that holds filtered nodes that are used by subsequent validations
  const nodes = toArray(cld.nodes) || [];
  const nodeInfo = {
    actionNodes: nodes.filter(node => node.name === 'Action'),
    checkNodes: nodes.filter(node => node.name === 'Check'),
    effectNodes: nodes.filter(node => node.name === 'Effect'),
    effectChainNodes: nodes.filter(node => node.name === 'EffectChain'),
    timerNodes: nodes.filter(node => node.name === 'Timer'),
    nodeMap: cld.nodes,
  };

  const validationResults = [
    validateBehaviorNodes(caseConf, nodeInfo, validationOptions),
    validateControlTypes(caseConf, nodeInfo, validationOptions),
    validateEmptyChecks(caseConf, nodeInfo, validationOptions),
    validateEndConditions(caseConf, nodeInfo, validationOptions),
    validateMedicationNodes(caseConf, nodeInfo, validationOptions),
    validateInfusionNodes(caseConf, nodeInfo, validationOptions),
    validateOxygenActions(caseConf, nodeInfo, validationOptions),
    validateOxygenChecks(caseConf, nodeInfo, validationOptions),
    validateIVChecks(caseConf, nodeInfo, validationOptions),
    validateInterventionNodes(caseConf, nodeInfo, validationOptions),
    validateMigratedNodes(caseConf, nodeInfo, validationOptions),
    validateMonitorNodes(caseConf, nodeInfo, validationOptions),
    validateMoulageNodes(caseConf, nodeInfo, validationOptions),
    validateNodeConnections(caseConf, nodeInfo, validationOptions),
    validateLabMediaNodes(caseConf, nodeInfo, validationOptions),
    validateProcedureControls(caseConf, nodeInfo, validationOptions),
    validateDialogueControls(caseConf, nodeInfo, validationOptions),
    validateTransferScene(caseConf, nodeInfo, validationOptions),
    validateCustomStateNodes(caseConf, nodeInfo, validationOptions),
    validateGradeEffectNodes(caseConf, nodeInfo, validationOptions),
    validateExposeNodes(caseConf, nodeInfo, validationOptions),
    validateCheckCounts(caseConf, nodeInfo, validationOptions),
    validatePulseStrengthChecks(caseConf, nodeInfo, validationOptions),
    validateGradingConflicts(caseConf, nodeInfo, validationOptions),
    validateTriggeringActions(caseConf, nodeInfo, validationOptions),
    validateCustomMenuNodes(caseConf, nodeInfo),
    validateAiNodes(caseConf, nodeInfo),
  ];

  validationResults.push(disabledResults);

  return mergeWith({}, ...validationResults, (objValue, srcValue) => {
    if (isArray(objValue)) {
      return objValue.concat(srcValue);
    }
    return undefined;
  });
}

export default validateCaseLogicDescription;
